'use client';

import { useId } from 'react';
import type { FormEventHandler } from 'react';
import { Switch } from '@1debit/chimekit';
import { CalculatorActions } from 'components/Widget/shared/CalculatorActions';
import { CALCULATOR_FORM_CARD } from 'components/Widget/shared/cardStyles';
import { CalculatorInput } from 'components/Widget/CalculatorInput/CalculatorInput';
import { RadioField } from 'components/Widget/shared/RadioField';
import { SelectField } from 'components/Widget/shared/SelectField';
import type { NumberFieldProps } from 'components/Widget/shared/useNumberFields';
import type { CurrencyFieldProps } from 'components/Widget/shared/useCurrencyFields';
import type { CalculatorField } from 'components/Widget/shared/types';
import {
  ANNUAL_SALARY_FIELD,
  ANNUAL_SALARY_SUFFIX,
  EXEMPTION_QUESTIONS,
  FORM_LABEL,
  HOURLY_WAGE_FIELD,
  HOURLY_WAGE_SUFFIX,
  OVERTIME_HOURS_FIELD,
  OVERTIME_SWITCH_LABEL,
  PAY_FREQUENCY_FIELD,
  PAY_FREQUENCY_OPTIONS,
  PAY_TYPE_LEGEND,
  PAY_TYPE_OPTIONS,
  WEEKLY_HOURS_FIELD,
  YES_NO_OPTIONS,
} from './constants';
import type { ExemptionKey, PaycheckExemptions, PaycheckFormErrors, PayFrequency, PayType } from './types';

const DIVIDER = 'border-content-tertiary/20';

const FIELD_PAIR = 'grid gap-6 sm:grid-cols-2 md:max-ml:grid-cols-1';

interface PaycheckCalculatorFormProps {
  errors: PaycheckFormErrors;
  currencyFieldProps: CurrencyFieldProps;
  hoursFieldProps: NumberFieldProps;
  /** Carries the live 1.5x greyed default, which the constant cannot. */
  overtimeWageField: CalculatorField;
  isOvertimeOn: boolean;
  payFrequency: PayFrequency | null;
  payType: PayType;
  exemptions: PaycheckExemptions;
  onPayFrequencyChange: (key: PayFrequency) => void;
  onPayTypeChange: (key: PayType) => void;
  onExemptionChange: (key: ExemptionKey, isExempt: boolean) => void;
  onOvertimeToggle: (isOn: boolean) => void;
  onCalculate: () => void;
  onReset: () => void;
}

export const PaycheckCalculatorForm = ({
  errors,
  currencyFieldProps,
  hoursFieldProps,
  overtimeWageField,
  isOvertimeOn,
  payFrequency,
  payType,
  exemptions,
  onPayFrequencyChange,
  onPayTypeChange,
  onExemptionChange,
  onOvertimeToggle,
  onCalculate,
  onReset,
}: PaycheckCalculatorFormProps) => {
  const overtimeSwitchId = useId();

  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    onCalculate();
  };

  return (
    <form aria-label={FORM_LABEL} className={CALCULATOR_FORM_CARD} onSubmit={handleSubmit}>
      <SelectField
        field={PAY_FREQUENCY_FIELD}
        options={PAY_FREQUENCY_OPTIONS}
        value={payFrequency ?? undefined}
        onChange={onPayFrequencyChange}
        error={errors.payFrequency}
      />

      <RadioField
        legend={PAY_TYPE_LEGEND}
        options={PAY_TYPE_OPTIONS}
        value={payType}
        onChange={option => onPayTypeChange(option.key)}
      />

      {payType === 'hourly' ? (
        <>
          <div className={FIELD_PAIR}>
            <CalculatorInput
              {...currencyFieldProps(HOURLY_WAGE_FIELD, errors.hourlyWage)}
              suffixLabel={HOURLY_WAGE_SUFFIX}
              collapsibleHint
            />
            <CalculatorInput {...hoursFieldProps(WEEKLY_HOURS_FIELD, errors.weeklyHours)} collapsibleHint />
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Switch
                id={overtimeSwitchId}
                value={isOvertimeOn}
                onValueChange={onOvertimeToggle}
                aria-labelledby={`${overtimeSwitchId}-label`}
              />
              <label id={`${overtimeSwitchId}-label`} htmlFor={overtimeSwitchId} className="text-body-saans">
                {OVERTIME_SWITCH_LABEL} (optional)
              </label>
            </div>

            {isOvertimeOn && (
              <div className={FIELD_PAIR}>
                <CalculatorInput
                  {...currencyFieldProps(overtimeWageField, errors.overtimeWage)}
                  suffixLabel={HOURLY_WAGE_SUFFIX}
                  collapsibleHint
                />
                <CalculatorInput {...hoursFieldProps(OVERTIME_HOURS_FIELD, errors.overtimeHours)} collapsibleHint />
              </div>
            )}
          </div>
        </>
      ) : (
        <CalculatorInput
          {...currencyFieldProps(ANNUAL_SALARY_FIELD, errors.annualSalary)}
          suffixLabel={ANNUAL_SALARY_SUFFIX}
          collapsibleHint
        />
      )}

      <hr className={DIVIDER} />

      {EXEMPTION_QUESTIONS.map(question => (
        <RadioField
          key={question.key}
          legend={question.legend}
          hint={question.hint}
          options={YES_NO_OPTIONS}
          value={exemptions[question.key] ? 'yes' : 'no'}
          onChange={option => onExemptionChange(question.key, option.key === 'yes')}
        />
      ))}

      <hr className={DIVIDER} />

      <CalculatorActions onReset={onReset} />
    </form>
  );
};
